/**
 * AuthContext — wraps oidc-client-ts UserManager in React context.
 *
 * 1:1 von InvoiceAI uebernommen
 * (../../invoiceai/frontend/src/contexts/AuthContext.tsx).
 *
 * - OIDC Authorization-Code + PKCE direkt gegen Keycloak.
 * - Token im Memory (kein localStorage), Refresh per Silent-Renew.
 * - login() = Redirect zum Platform-Hub für Workspace-Onboarding.
 * - loginDirect() = Direkt-Keycloak-Login (Fallback).
 * - Bestehende Komponenten nutzen `useAuth()` — Shim erhaelt
 *   Backward-Compat-Felder (user, loading) basierend auf den OIDC-Daten.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  userManager,
  getTaxPilotClaims,
  redirectToHub,
  type OidcUser,
  type TaxPilotClaims,
  type TaxPilotMembership,
} from "@/lib/auth";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

export interface AuthState {
  oidcUser: OidcUser | null;
  isLoading: boolean;
  loading: boolean; // alias für Backward-Compat
  isAuthenticated: boolean;
  taxpilotClaims: TaxPilotClaims | null;
  memberships: TaxPilotMembership[];
  user: AuthUser | null;
  error: string | null;
  /** Primary login → redirect to Platform Hub. */
  login: () => void;
  /** Direct OIDC login (fallback). */
  loginDirect: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [oidcUser, setOidcUser] = useState<OidcUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleInit = useCallback(async () => {
    try {
      const path = window.location.pathname;

      if (path === "/auth/silent-renew") {
        await userManager.signinSilentCallback();
        return;
      }

      if (path === "/auth/callback") {
        // CallbackPage handles the code exchange itself.
        return;
      }

      const user = await userManager.getUser();
      if (user && !user.expired) {
        setOidcUser(user);
      }
    } catch (err) {
      console.warn("Auth init error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void handleInit();

    const onUserLoaded = (user: OidcUser) => {
      setOidcUser(user);
      setIsLoading(false);
    };
    const onUserUnloaded = () => setOidcUser(null);
    const onSilentRenewError = (err: Error) => {
      console.warn("Silent renew failed:", err);
      setOidcUser(null);
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [handleInit]);

  const login = useCallback(() => {
    redirectToHub();
  }, []);

  const loginDirect = useCallback(async () => {
    await userManager.signinRedirect({
      state: window.location.pathname + window.location.search,
    });
  }, []);

  const logout = useCallback(async () => {
    await userManager.signoutRedirect();
  }, []);

  const taxpilotClaims = oidcUser?.access_token
    ? getTaxPilotClaims(oidcUser.access_token)
    : null;

  const memberships = taxpilotClaims?.memberships ?? [];

  const user: AuthUser | null = oidcUser
    ? {
        id: taxpilotClaims?.user_id ?? oidcUser.profile.sub,
        email: oidcUser.profile.email ?? "",
        name:
          oidcUser.profile.name ?? oidcUser.profile.email ?? "Unbekannt",
      }
    : null;

  const value: AuthState = {
    oidcUser,
    isLoading,
    loading: isLoading,
    isAuthenticated: !!oidcUser && !oidcUser.expired,
    taxpilotClaims,
    memberships,
    user,
    error,
    login,
    loginDirect,
    logout,
    refresh: handleInit,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuthContext(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuthContext must be used inside AuthProvider");
  return ctx;
}

/** Backward-compat alias used across the existing component tree. */
export function useAuth(): AuthState {
  return useAuthContext();
}
